/**
 * MatchupEditorModal - Modal for editing playoff bracket matchups
 * Allows setting date, time, location, and broadcast info for bracket games
 */
import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { Button } from "../../ui/button";

export const MatchupEditorModal = ({ matchup, playoff, teams, leagueId, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    ...matchup,
    game_date: matchup.game_date || "",
    game_time: matchup.game_time || "",
    location: matchup.location || "",
    tv_network: matchup.tv_network || "",
    youtube_url: matchup.youtube_url || "",
    team1_score: matchup.team1_score ?? "",
    team2_score: matchup.team2_score ?? ""
  });
  const [saving, setSaving] = useState(false);

  const getTeamName = (teamId) => {
    if (!teamId) return "TBD";
    const team = teams.find(t => t.id === teamId);
    return team?.name || "TBD";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Calculate winner if both scores are provided
    let winner_id = formData.winner_id;
    const t1Score = formData.team1_score !== "" ? parseInt(formData.team1_score) : null;
    const t2Score = formData.team2_score !== "" ? parseInt(formData.team2_score) : null;
    
    if (t1Score !== null && t2Score !== null) {
      if (t1Score > t2Score) winner_id = formData.team1_id;
      else if (t2Score > t1Score) winner_id = formData.team2_id;
    }
    
    await onSave({
      ...formData,
      team1_score: t1Score,
      team2_score: t2Score,
      winner_id
    });
    setSaving(false);
  };

  const roundName = matchup.round_name || `Round ${matchup.round}`;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-xl border border-zinc-800 w-full max-w-lg">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">Edit Game</h3>
            <p className="text-xs text-zinc-500">{playoff.name} • {roundName}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Teams Display */}
          <div className="bg-zinc-800/50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-medium">{getTeamName(formData.team1_id)}</span>
              <input
                type="number"
                value={formData.team1_score}
                onChange={(e) => setFormData({ ...formData, team1_score: e.target.value })}
                className="w-16 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-white text-center"
                placeholder="-"
                min="0"
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white font-medium">{getTeamName(formData.team2_id)}</span>
              <input
                type="number"
                value={formData.team2_score}
                onChange={(e) => setFormData({ ...formData, team2_score: e.target.value })}
                className="w-16 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-white text-center"
                placeholder="-"
                min="0"
              />
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Date</label>
              <input
                type="date"
                value={formData.game_date}
                onChange={(e) => setFormData({ ...formData, game_date: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Time</label>
              <input
                type="time"
                value={formData.game_time}
                onChange={(e) => setFormData({ ...formData, game_time: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
              placeholder="Venue name"
            />
          </div>

          {/* Broadcast */}
          <div className="space-y-3 pt-3 border-t border-zinc-800">
            <p className="text-xs font-medium text-zinc-500">Broadcast (Optional)</p>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={formData.tv_network}
                onChange={(e) => setFormData({ ...formData, tv_network: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                placeholder="TV Network"
              />
              <input
                type="url"
                value={formData.youtube_url}
                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-white text-sm"
                placeholder="Stream URL"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-800">
            <Button type="button" variant="outline" onClick={onClose} className="text-sm">Cancel</Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600 text-sm"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MatchupEditorModal;
